import React, { useEffect, useState, useMemo } from 'react';
import moment from 'moment/moment';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import validationSchema from './schema';
import {
  formFieldContainerStyles,
  formControlStyles,
  taxItemContainerStyles,
  taxDeleteButtonContainerStyles,
  taxDeleteButtonStyles,
} from './style';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import FormField from '../../../components/form/FormField';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Autocomplete from '@mui/material/Autocomplete';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';

import MonetizationOn from '@mui/icons-material/MonetizationOn';
import Delete from '@mui/icons-material/Delete';

import SelectDueDate from '../../../components/selectDueDate';
import YearMonthPicker from '../../../components/yearMonthPicker';
import ActionButton from '../../../components/actionButton';

import {
  CREATE_CONTRACT,
  GET_ALL_CONTRACTS,
  GET_CONTRACT_BY_ID,
  UPDATE_CONTRACT,
} from '../../../gql/contracts';
import { GET_ALL_PRODUCTS_NAMES } from '../../../gql/products';
import { GET_ALL_USERS } from '../../../gql/users';
import { GET_ALL_PARTNERS } from '../../../gql/partners';
import { GET_ALL_TAXES } from '../../../gql/taxes';

import { getTenantId } from '../../../services/tenancy';
import { getTierLabel } from '../../../utils/tiers';
import usePermissions from '../../../hooks/usePermissions';
import useSnackbar from '../../../hooks/useSnackbar';

const ContractForm = ({ uid }: { uid?: string }) => {
  const [showModal, setShowModal] = useState(false);
  const [taxInputValue, setTaxInputValue] = useState('');

  const { isAdmin, isOwner } = usePermissions();

  let { id } = useParams();
  id = uid ?? id;
  const isCreate = id === 'create';
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const [mutateContract] = useMutation(isCreate ? CREATE_CONTRACT : UPDATE_CONTRACT, {
    refetchQueries: [{ query: GET_ALL_CONTRACTS }],
  });
  const { data: productData } = useQuery(GET_ALL_PRODUCTS_NAMES);
  const { data: usersData } = useQuery(GET_ALL_USERS);
  const { data: partnerData } = useQuery(GET_ALL_PARTNERS);
  const { data: taxesData, loading: loadingTaxes } = useQuery(GET_ALL_TAXES);
  const [getCurrentContract, { data }] = useLazyQuery(GET_CONTRACT_BY_ID, {
    fetchPolicy: 'no-cache',
  });
  const navigateBack = () => navigate('/dashboard/contracts');
  const [showRampUp, setShowRampUp] = useState<any>(false);
  const [showFreeTier, setShowFreeTier] = useState<any>(false);

  const handleSubmit: any = () => {
    snackbar.setGlobalLoading(true);
    const { values } = formik;
    snackbar.sendAlert({ level: 'info', message: 'Salvando Dados...' });

    const input: Record<string, any> = { tenantId: getTenantId() };
    Object.keys(validationSchema.fields).map((key) => {
      if (Object.hasOwn(values, key)) input[key] = values[key];
      return null;
    });

    const finalInput: Record<string, any> = {
      ...input,
      ...(isCreate ? {} : { id }),
      taxes: input?.taxes || [],
    };

    if (!isCreate) {
      finalInput.id = id;
    }

    mutateContract({ variables: { input: finalInput } })
      .then(() =>
        snackbar.sendAlert({ level: 'success', message: 'Dados salvos com sucesso ' })
      )
      .catch((er) => {
        snackbar.sendAlert({ level: 'error', message: 'Erro Salvando dados' });
        console.error(er);
      })
      .finally(() => {
        snackbar.setGlobalLoading(false);
        navigateBack();
      });
  };
  const getDefaultNotificationEmails = (contractData: any) => {
    const defaultEmails: string[] = [];

    if (contractData?.partner?.email) {
      defaultEmails.push(contractData.partner.email.toLowerCase());
    }

    return defaultEmails;
  };

  const formik = useFormik({
    initialValues: data?.getContractById
      ? {
          ...data.getContractById,
          contractId: data.getContractById?.id,
          taxes: data.getContractById?.taxes ?? [],
          dueDates: data.getContractById.dueDates?.[0] ?? 15,
          credit: data.getContractById.credit ?? [],
          currency: data.getContractById.currency,
          notificationEmails:
            data.getContractById?.notificationEmails?.length > 0
              ? data.getContractById.notificationEmails
              : getDefaultNotificationEmails(data.getContractById),
        }
      : {
          taxes: [],
          dueDates: 15,
          credit: [],
          currency: 'BRL',
          notificationEmails: [],
        },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validateOnMount: false,
    validateOnChange: false,
    validateOnBlur: false,
  });
  const hideAutoComplete = () => setShowModal(false);

  const handleAutoComplete = () => setShowModal(true);

  const removeTax = (resourceIndex: number) => () => {
    const { taxes } = formik.values;
    const newData = [...taxes];
    newData.splice(resourceIndex, 1);
    formik.setFieldValue('taxes', [...newData]);
  };
  const handleChangeAutoComplete = (_: unknown, value: Record<string, string>) => {
    const { taxes } = formik.values;
    const newTax = { ...value, name: value.name ?? value.description ?? '--' };
    formik.setFieldValue('taxes', [...taxes, newTax]);
    hideAutoComplete();
  };
  const sortPartner = (partners: any) =>
    partners
      .slice()
      .sort(compare)
      .map((item: Record<any, any>) => (
        <MenuItem value={item.id} key={item.id}>
          {item.tradingName}
        </MenuItem>
      ));
  const compare = (a: any, b: any) => {
    if (a.tradingName < b.tradingName) {
      return -1;
    }
    if (a.tradingName > b.tradingName) {
      return 1;
    }
    return 0;
  };

  useEffect(() => {
    if (!isCreate) {
      getCurrentContract({ variables: { id } }).then(({ data }) => {
        const { getContractById } = data;
        setShowRampUp(Boolean(getContractById.mmcFreeUntil));
        setShowFreeTier(Boolean(getContractById.freeUntil));
      });
    }
  }, [isCreate]);

  useEffect(() => {
    if (data?.getContractById && !data.getContractById.notificationEmails?.length) {
      const defaultEmails = getDefaultNotificationEmails(data.getContractById);
      if (defaultEmails.length > 0) {
        formik.setFieldValue('notificationEmails', defaultEmails);
      }
    }
  }, [data?.getContractById]);

  const toggleRampUp = () => {
    const newState = !showRampUp;
    setShowRampUp(newState);
    if (!newState) {
      formik.setFieldValue('mmcFreeUntil', null);
    } else {
      formik.setFieldValue('mmcFreeUntil', moment(new Date()).toISOString());
    }
  };

  const toggleFreeTier = () => {
    const newState = !showFreeTier;
    setShowFreeTier(newState);
    if (!newState) {
      formik.setFieldValue('freeUntil', null);
    } else {
      formik.setFieldValue('freeUntil', moment(new Date()).toISOString());
    }
  };

  const handleDateChangeRampUp = (rampUpDate: any) =>
    formik.setFieldValue('mmcFreeUntil', rampUpDate);

  const handleDateChangeFree = (freeDate: any) =>
    formik.setFieldValue('freeUntil', freeDate);

  const tiers: Record<any, any>[] = useMemo(() => {
    if (productData && formik.values?.productId) {
      const selectedProduct = productData?.getAllProducts.find(
        (product: any) => product.id === formik.values.productId
      );

      if (selectedProduct) {
        return [...selectedProduct.tiers].sort((a: any, b: any) => a.tier - b.tier);
      }
    }
    return [];
  }, [productData, formik.values]);

  return (
    <>
      <Dialog open={showModal} onClose={hideAutoComplete}>
        <DialogTitle>Busca de Impostos</DialogTitle>
        <DialogContent>
          <DialogContentText>Selecione abaixo um imposto pré-definido</DialogContentText>
          <Autocomplete
            onChange={handleChangeAutoComplete}
            getOptionLabel={(option: any) => option.name ?? option.description}
            loading={loadingTaxes}
            options={taxesData?.getAllTaxes ?? []}
            sx={{ width: 300 }}
            renderInput={(params: any) => <TextField {...params} label="Imposto" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={hideAutoComplete}>Cancelar</Button>
        </DialogActions>
      </Dialog>
      <Paper sx={{ width: '100%', mb: 2, p: 2 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={4}>
            <Grid container item spacing={2}>
              <Grid container item spacing={2} xs={4} sx={{ height: 'fit-content' }}>
                <Divider textAlign="left" sx={{ m: 1, pt: 4 }}>
                  <Typography variant="h5">Contrato</Typography>
                </Divider>
                <Grid item xs={12} sx={formFieldContainerStyles}>
                  <FormField
                    label="Produto"
                    error={Boolean(formik.errors.productId)}
                    helperText={formik.errors.productId as string}
                    required
                    disabled={!isAdmin}
                  >
                    <Select
                      labelId="productId"
                      id="productId"
                      value={formik.values.productId ?? ''}
                      name="productId"
                      variant="standard"
                      onChange={formik.handleChange}
                      error={Boolean(formik.errors.productId)}
                      sx={{
                        '& .MuiFormHelperText-root': {
                          position: 'absolute',
                          bottom: '-20px',
                          left: 0,
                          margin: 0,
                          fontSize: '0.75rem',
                        },
                      }}
                    >
                      {productData?.getAllProducts?.map((item: Record<any, any>) => (
                        <MenuItem value={item.id} key={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormField>
                </Grid>
                <Grid item xs={12} sx={formFieldContainerStyles}>
                  <FormField
                    label="Tier"
                    error={Boolean(formik.errors.product_tier)}
                    helperText={formik.errors.product_tier as string}
                    required
                    disabled={!isAdmin}
                  >
                    <Select
                      fullWidth
                      name="product_tier"
                      variant="standard"
                      onChange={formik.handleChange}
                      value={formik.values.product_tier ?? ''}
                      error={Boolean(formik.errors.product_tier)}
                      sx={{
                        '& .MuiFormHelperText-root': {
                          position: 'absolute',
                          bottom: '-20px',
                          left: 0,
                          margin: 0,
                          fontSize: '0.75rem',
                        },
                      }}
                    >
                      {tiers.map(({ tier, description }) => (
                        <MenuItem value={tier} key={description || getTierLabel(tier)}>
                          <Typography>{description || getTierLabel(tier)}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormField>
                </Grid>
                <Grid item xs={12} sx={formFieldContainerStyles}>
                  <FormField
                    label="Partner"
                    error={Boolean(formik.errors.partnerId)}
                    helperText={formik.errors.partnerId as string}
                    required
                    disabled={!isAdmin}
                  >
                    {partnerData && (
                      <Select
                        fullWidth
                        labelId="partnerId"
                        id="partnerId"
                        value={formik.values.partnerId ?? ''}
                        name="partnerId"
                        variant="standard"
                        onChange={formik.handleChange}
                        error={Boolean(formik.errors.partnerId)}
                        sx={{
                          '& .MuiFormHelperText-root': {
                            position: 'absolute',
                            bottom: '-20px',
                            left: 0,
                            margin: 0,
                            fontSize: '0.75rem',
                          },
                        }}
                        MenuProps={{
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                          classes: { paper: 'menu-paper' },
                          MenuListProps: {
                            style: { maxHeight: 192, overflow: 'auto' },
                          },
                          style: { width: 200 },
                        }}
                      >
                        {sortPartner(partnerData.getAllPartners)}
                      </Select>
                    )}
                  </FormField>
                </Grid>
                <Grid item xs={12} sx={formFieldContainerStyles}>
                  <FormField
                    label="Vendedor"
                    error={Boolean(formik.errors.sellerId)}
                    helperText={formik.errors.sellerId as string}
                    required
                    disabled={!isAdmin}
                  >
                    <Select
                      labelId="sellerId"
                      id="sellerId"
                      value={formik.values.sellerId ?? ''}
                      name="sellerId"
                      variant="standard"
                      onChange={formik.handleChange}
                      error={Boolean(formik.errors.sellerId)}
                      sx={{
                        '& .MuiFormHelperText-root': {
                          position: 'absolute',
                          bottom: '-20px',
                          left: 0,
                          margin: 0,
                          fontSize: '0.75rem',
                        },
                      }}
                    >
                      {usersData?.getAllUsers?.map((item: Record<any, any>) => (
                        <MenuItem value={item.id} key={item.id}>
                          {item.firstName} {item.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormField>
                </Grid>
              </Grid>
              {(isOwner(data?.getContractById?.sellerId) || isAdmin) && (
                <Grid container item spacing={2} xs={4} sx={{ height: 'fit-content' }}>
                  <Divider textAlign="left" sx={{ m: 1, pt: 4 }}>
                    <Typography variant="h5">Pagamento</Typography>
                  </Divider>

                  <Grid item xs={12} sx={{ mb: 1, height: '60px', position: 'relative' }}>
                    <FormField
                      label="Pagamento"
                      error={Boolean(formik.errors.paymentType)}
                      helperText={formik.errors.paymentType as string}
                      required
                      disabled={!isAdmin}
                    >
                      <Select
                        labelId="paymentType"
                        id="paymentType"
                        value={formik.values.paymentType ?? ''}
                        name="paymentType"
                        variant="standard"
                        onChange={(value) => formik.handleChange(value)}
                        error={Boolean(formik.errors.paymentType)}
                        sx={{
                          '& .MuiFormHelperText-root': {
                            position: 'absolute',
                            bottom: '-20px',
                            left: 0,
                            margin: 0,
                            fontSize: '0.75rem',
                          },
                        }}
                      >
                        {['BOLETO ITAÚ', 'BOLETO BRADESCO'].map((item: string) => (
                          <MenuItem value={item} key={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormField>
                  </Grid>

                  <Grid item xs={12} sx={{ mb: 1, height: '60px', position: 'relative' }}>
                    <FormField
                      label="Moeda"
                      error={Boolean(formik.errors.currency)}
                      helperText={formik.errors.currency as string}
                      required
                      disabled={!isAdmin}
                    >
                      <Select
                        labelId="currency"
                        id="currency"
                        value={formik.values.currency}
                        name="currency"
                        variant="standard"
                        onChange={formik.handleChange}
                        error={Boolean(formik.errors.currency)}
                        sx={{
                          '& .MuiFormHelperText-root': {
                            position: 'absolute',
                            bottom: '-20px',
                            left: 0,
                            margin: 0,
                            fontSize: '0.75rem',
                          },
                        }}
                      >
                        <MenuItem value="BRL">R$ - BRL</MenuItem>
                        <MenuItem value="USD">US$ - USD</MenuItem>
                      </Select>
                    </FormField>
                  </Grid>

                  <Grid item xs={12} sx={{ mb: 1, height: '60px', position: 'relative' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={formik.values.dollarType !== 'PTAX' ? 6 : 12}>
                        <FormField
                          label="Dollar"
                          error={Boolean(formik.errors.dollarType)}
                          helperText={formik.errors.dollarType as string}
                          required
                          disabled={!isAdmin}
                        >
                          <Select
                            labelId="dollarType"
                            id="dollarType"
                            value={formik.values.dollarType ?? ''}
                            name="dollarType"
                            variant="standard"
                            onChange={formik.handleChange}
                            error={Boolean(formik.errors.dollarType)}
                            sx={{
                              '& .MuiFormHelperText-root': {
                                position: 'absolute',
                                bottom: '-20px',
                                left: 0,
                                margin: 0,
                                fontSize: '0.75rem',
                              },
                            }}
                          >
                            {['PTAX', 'LPTAX', 'FIXED'].map((item: string) => (
                              <MenuItem value={item} key={item}>
                                {item}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormField>
                      </Grid>

                      {formik.values.dollarType !== 'PTAX' && (
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            autoFocus
                            variant="standard"
                            name="dollarValue"
                            type="number"
                            label="Valor"
                            value={formik.values.dollarValue ?? ''}
                            onChange={formik.handleChange}
                            error={Boolean(formik.errors.dollarValue)}
                            helperText={formik.errors.dollarValue as string}
                            required
                            disabled={!isAdmin || formik.values.dollarType === 'PTAX'}
                            sx={{
                              '& .MuiInput-underline:before': {
                                borderBottomColor: formik.errors.dollarValue
                                  ? '#d32f2f'
                                  : undefined,
                              },
                              '& .MuiFormHelperText-root': {
                                position: 'absolute',
                                bottom: '-20px',
                                left: 0,
                                margin: 0,
                                fontSize: '0.75rem',
                              },
                            }}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sx={{ mb: 1, height: '60px', position: 'relative' }}>
                    <FormField
                      label="Dia de pagamento"
                      error={Boolean(formik.errors.dueDates)}
                      helperText={formik.errors.dueDates as string}
                      required
                      disabled={!isAdmin}
                    >
                      <SelectDueDate
                        value={formik.values.dueDates}
                        onChange={formik.handleChange}
                        error={Boolean(formik.errors.dueDates)}
                        name="dueDates"
                        disabled={!isAdmin}
                        sx={formControlStyles}
                      />
                    </FormField>
                  </Grid>
                </Grid>
              )}
              <Grid container item spacing={2} xs={4} sx={{ height: 'fit-content' }}>
                <Divider textAlign="left" sx={{ m: 1, pt: 4 }}>
                  <Typography variant="h5">Notificações</Typography>
                </Divider>

                <Grid item xs={12} sx={{ mb: 1, height: '80px', position: 'relative' }}>
                  <Autocomplete
                    multiple
                    disabled={!isAdmin}
                    options={(() => {
                      const emails: string[] = [];
                      const mainEmail =
                        data?.getContractById?.partner?.email?.toLowerCase();
                      const selectedEmails = (formik.values.notificationEmails || []).map(
                        (email: string) => email.toLowerCase()
                      );

                      if (data?.getContractById?.partner?.contacts) {
                        data.getContractById.partner.contacts.forEach((contact: any) => {
                          const contactEmail = contact.email?.toLowerCase();
                          if (
                            contactEmail &&
                            !emails.includes(contactEmail) &&
                            contactEmail !== mainEmail &&
                            !selectedEmails.includes(contactEmail)
                          ) {
                            emails.push(contactEmail);
                          }
                        });
                      }

                      if (mainEmail && !selectedEmails.includes(mainEmail)) {
                        emails.push(mainEmail);
                      }

                      return emails;
                    })()}
                    sx={{
                      '& .MuiInput-underline:before': {
                        borderBottomColor: formik.errors.notificationEmails
                          ? '#d32f2f'
                          : undefined,
                      },
                    }}
                    renderOption={(props, option) => {
                      const mainEmail = data?.getContractById?.partner?.email;
                      const isSelected =
                        formik.values.notificationEmails?.includes(option);

                      if (option === mainEmail) {
                        return (
                          <li {...props}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                backgroundColor: isSelected ? '#e8f5e9' : 'transparent',
                                borderRadius: '4px',
                              }}
                            >
                              <span>{option}</span>
                              <Chip
                                label="E-mail principal"
                                size="small"
                                sx={{
                                  backgroundColor: isSelected ? '#4CAF50' : '#e8f5e9',
                                  color: isSelected ? '#ffffff' : '#4CAF50',
                                  borderRadius: '16px',
                                  fontSize: '11px',
                                  height: '20px',
                                  border: `1px solid ${
                                    isSelected ? '#4CAF50' : '#4CAF50'
                                  }`,
                                  marginLeft: '8px',
                                  '& .MuiChip-label': {
                                    padding: '0 8px',
                                  },
                                }}
                              />
                            </div>
                          </li>
                        );
                      }
                      return <li {...props}>{option}</li>;
                    }}
                    value={formik.values.notificationEmails ?? []}
                    onChange={(_, newValue) => {
                      formik.setFieldValue(
                        'notificationEmails',
                        newValue.map((email: string) => email.toLowerCase())
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label="E-mails para Notificações"
                        error={
                          formik.touched.notificationEmails &&
                          Boolean(formik.errors.notificationEmails)
                        }
                        helperText={formik.errors.notificationEmails}
                        autoComplete="off"
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: 'new-password',
                        }}
                        sx={{
                          '& .MuiFormHelperText-root': {
                            position: 'absolute',
                            bottom: '-20px',
                            left: 0,
                            margin: 0,
                            fontSize: '0.75rem',
                          },
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          label={option}
                          size="small"
                          sx={{
                            backgroundColor: '#f5f5f5',
                            borderRadius: '16px',
                            fontSize: '11px',
                            height: '20px',
                            '& .MuiChip-label': {
                              padding: '0 8px',
                            },
                          }}
                        />
                      ))
                    }
                    filterOptions={(options, { inputValue }) => {
                      const filterValue = inputValue.toLowerCase();
                      return options.filter((option) =>
                        option.toLowerCase().includes(filterValue)
                      );
                    }}
                    ListboxProps={{
                      style: { maxHeight: 300 },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container item spacing={2}>
                <Grid item xs={4}>
                  <Stack alignItems="center" spacing={2} direction="row">
                    <Checkbox
                      checked={showRampUp}
                      onChange={toggleRampUp}
                      disabled={!isAdmin}
                    />
                    Ramp-up
                    {showRampUp && (
                      <YearMonthPicker
                        disabled={!isAdmin}
                        label="Ramp-up"
                        onChange={handleDateChangeRampUp}
                        value={formik.values.mmcFreeUntil}
                      />
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={4} sx={{ mr: '20px' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      checked={showFreeTier}
                      onChange={toggleFreeTier}
                      disabled={!isAdmin}
                      sx={{ padding: '0px', paddingRight: '10px' }}
                    />
                    Gratuidade
                    {showFreeTier && (
                      <YearMonthPicker
                        label="Gratuidade"
                        onChange={handleDateChangeFree}
                        value={formik.values.freeUntil}
                        disabled={!isAdmin}
                      />
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={4} />
              </Grid>
            </Grid>
            <Grid item container spacing={4}>
              <Grid item xs={12}>
                <Typography variant="h4" sx={{ marginBottom: '20px' }}>
                  Impostos
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={9}>
                    <Autocomplete
                      options={
                        taxesData?.getAllTaxes?.filter(
                          (tax: any) =>
                            !formik.values.taxes?.some(
                              (selectedTax: any) =>
                                selectedTax.description === tax.description &&
                                selectedTax.type === tax.type &&
                                selectedTax.value === tax.value
                            )
                        ) ?? []
                      }
                      getOptionLabel={(option: any) =>
                        `${option.description} (${
                          option.type === 'fixed' ? 'Fixo' : 'Porcentagem'
                        }: ${option.value})`
                      }
                      value={null}
                      inputValue={taxInputValue}
                      freeSolo={false}
                      disableClearable
                      onChange={(_, newValue) => {
                        if (newValue) {
                          const alreadyExists = formik.values.taxes?.some(
                            (tax: any) =>
                              tax.description === newValue.description &&
                              tax.type === newValue.type &&
                              tax.value === newValue.value
                          );

                          if (!alreadyExists) {
                            formik.setFieldValue('taxes', [
                              ...(formik.values.taxes || []),
                              {
                                id: newValue.id,
                                name: newValue.description,
                                description: newValue.description,
                                type: newValue.type,
                                value: newValue.value,
                                isPublic: newValue.isPublic,
                              },
                            ]);
                            // Limpa o input após selecionar
                            setTaxInputValue('');
                          }
                        }
                      }}
                      onInputChange={(_, newInputValue, reason) => {
                        if (reason === 'reset') {
                          setTaxInputValue('');
                        } else {
                          setTaxInputValue(newInputValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Adicionar Imposto"
                          variant="standard"
                          fullWidth
                          inputProps={{
                            ...params.inputProps,
                            readOnly: true,
                          }}
                          sx={{
                            '& .MuiInputBase-root': {
                              cursor: 'pointer !important',
                              '& input': {
                                cursor: 'pointer !important',
                              },
                              '&:hover': {
                                '& .MuiInput-underline:before': {
                                  borderBottomColor: '#4caf50',
                                },
                              },
                            },
                          }}
                        />
                      )}
                      sx={{
                        '& .MuiAutocomplete-popupIndicator': {
                          cursor: 'pointer',
                        },
                        '& .MuiAutocomplete-clearIndicator': {
                          cursor: 'pointer',
                        },
                        '& .MuiAutocomplete-listbox .MuiAutocomplete-option': {
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#4caf50 !important',
                            color: 'white',
                          },
                          '&[aria-selected="true"]': {
                            backgroundColor: '#4caf50 !important',
                            color: 'white',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={2} />
                </Grid>

                {formik.values.taxes.length === 0 ? (
                  <Grid item xs={12}>
                    <Typography variant="body1">Não há impostos aplicados</Typography>
                  </Grid>
                ) : (
                  formik.values.taxes.map((tax: any, index: number) => (
                    <Grid
                      item
                      xs={12}
                      sx={taxItemContainerStyles}
                      key={`tax_${index}`}
                      container
                      spacing={2}
                    >
                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          variant="standard"
                          name={`taxes.[${index}].name`}
                          label="Nome do Imposto"
                          value={
                            formik.values.taxes?.[index]?.name ||
                            formik.values.taxes?.[index]?.description ||
                            ''
                          }
                          InputProps={{
                            readOnly: true,
                          }}
                          error={Boolean(formik.errors.taxes)}
                          helperText={formik.errors.taxes}
                          sx={formControlStyles}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          variant="standard"
                          name={`taxes.[${index}].description`}
                          label="Descrição do Imposto"
                          value={formik.values.taxes?.[index]?.description ?? ''}
                          InputProps={{
                            readOnly: true,
                          }}
                          error={Boolean(formik.errors.taxes)}
                          helperText={formik.errors.taxes}
                          sx={formControlStyles}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <FormControl fullWidth>
                          <InputLabel
                            sx={{ transform: 'translate(0px,0px) scale(0.75)' }}
                            id="typel"
                          >
                            Tipo do Imposto
                          </InputLabel>
                          <Select
                            labelId="type"
                            label="Tipo do Imposto"
                            name={`taxes.[${index}].type`}
                            variant="standard"
                            value={formik.values.taxes?.[index]?.type ?? ''}
                            inputProps={{
                              readOnly: true,
                            }}
                            error={Boolean(formik.errors.taxes)}
                          >
                            <MenuItem value="fixed">Fixo</MenuItem>
                            <MenuItem value="percentage">Por Porcentagem</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={1}>
                        <TextField
                          fullWidth
                          type="number"
                          variant="standard"
                          name={`taxes.[${index}].value`}
                          label="Valor"
                          value={formik.values.taxes?.[index]?.value ?? ''}
                          InputProps={{
                            readOnly: true,
                          }}
                          error={Boolean(formik.errors.taxes)}
                          helperText={formik.errors.taxes}
                          sx={formControlStyles}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={1}
                        sx={taxDeleteButtonContainerStyles}
                      >
                        <Button
                          size="medium"
                          onClick={removeTax(index)}
                          variant="contained"
                          sx={taxDeleteButtonStyles}
                        >
                          <Delete />
                        </Button>
                      </Grid>
                      <Grid item xs={2} />
                    </Grid>
                  ))
                )}
              </Grid>
            </Grid>
            <Grid
              item
              container
              spacing={2}
              xs={12}
              justifyContent="flex-end"
              sx={{ position: 'inherit' }}
            >
              {isAdmin && (
                <ActionButton
                  data-testid="create-tax-button"
                  title="Criar novo Imposto"
                  onClick={handleAutoComplete}
                  typography={<MonetizationOn fontSize="large" />}
                />
              )}
            </Grid>
            <Grid item container spacing={2} xs={12} justifyContent="flex-end">
              <Grid item>
                <Button size="large" onClick={navigateBack} data-testid="cancel-contract">
                  Cancelar
                </Button>
              </Grid>
              <Grid item>
                {isAdmin && (
                  <Button
                    variant="contained"
                    size="large"
                    type="submit"
                    data-testid="save-contract-button"
                  >
                    Salvar
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </>
  );
};

export default ContractForm;
